class FeaturedSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.isLooping = this.dataset.loop === 'true';
    this.gapMob = parseInt(this.dataset.gapMob) || 0;
    this.gapDesk = parseInt(this.dataset.gapDesk) || 0;
    this.autoScrollSpeed = parseInt(this.dataset.autoScrollSpeed) || 0; // 0 = disabled
    this.transitionSpeed = parseFloat(this.dataset.transitionSpeed) * 1000 || 800; // Convert seconds to ms
    this.swiperInstance = null;
    this.observer = null;

    // Bind the resize handler so we can add/remove it cleanly
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    // Theme Editor support: Init immediately for better editor UX
    if (Shopify.designMode) {
      this.checkAndInitSwiper();
      window.addEventListener('resize', this.handleResize);
      this.addEventListener('shopify:block:select', this.onBlockSelect.bind(this));
      return;
    }

    // Lazy load initialization 1000px before the section is visible
    const observerOptions = {
      root: null,
      rootMargin: '1000px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.checkAndInitSwiper();
          window.addEventListener('resize', this.handleResize);
          observer.unobserve(this);
        }
      });
    }, observerOptions);

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);
    this.destroySwiper();
  }

  handleResize() {
    this.checkAndInitSwiper();
  }

  // Dynamically load Swiper JS and CSS only when needed
  loadSwiperLibrary() {
    return new Promise((resolve, reject) => {
      // If another instance already started loading the script, wait for it
      if (document.querySelector('#dynamic-swiper-script')) {
        const checkInterval = setInterval(() => {
          if (typeof Swiper !== 'undefined') {
            clearInterval(checkInterval);
            resolve();
          }
        }, 50);
        return;
      }

      // Load CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
      document.head.appendChild(link);

      // Load JS
      const script = document.createElement('script');
      script.id = 'dynamic-swiper-script';
      script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async checkAndInitSwiper() {
    const wrapper = this.querySelector('.swiper-wrapper');
    const slides = Array.from(this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)'));
    if (slides.length === 0 || !wrapper) return;

    // Calculate total width of items + gaps based on current screen size
    const isDesktop = window.innerWidth >= 769;
    const currentGap = isDesktop ? this.gapDesk : this.gapMob;

    let totalSlidesWidth = 0;
    slides.forEach(slide => {
      totalSlidesWidth += slide.offsetWidth;
    });

    // Total width including the requested gap settings between items
    const totalWidth = totalSlidesWidth + ((slides.length - 1) * currentGap);

    // 1 & 2. Check if slides are wider than the container element
    if (totalWidth > this.clientWidth) {

      // Load Swiper dynamically if it doesn't exist
      if (typeof Swiper === 'undefined') {
        try {
          await this.loadSwiperLibrary();
        } catch (error) {
          console.error('Failed to load Swiper library.', error);
          return;
        }
      }

      if (!this.swiperInstance) {
        // Clear native flex fallbacks before initializing Swiper
        wrapper.style.justifyContent = '';
        wrapper.style.gap = '';
        this.initSwiper();
      } else {
        this.swiperInstance.update();
      }
    } else {
      // Slides fit on screen: no need for Swiper initialization
      this.destroySwiper();

      // Apply native flexbox fallback to center the slides gracefully
      wrapper.style.justifyContent = 'center';
      wrapper.style.gap = `${currentGap}px`;
    }
  }

  initSwiper() {
    if (typeof Swiper === 'undefined') return;

    const swiperOptions = {
      slidesPerView: 'auto',
      spaceBetween: this.gapMob, // Set root value (mobile default)
      loop: this.isLooping,
      grabCursor: true,
      speed: this.transitionSpeed, // Apply the transition speed dynamically
      breakpoints: {
        769: {
          spaceBetween: this.gapDesk // Override for desktop
        }
      },
      on: {
        resize: () => {
          if (this.swiperInstance) {
            this.swiperInstance.update();
          }
        },
        orientationchange: () => {
          setTimeout(() => {
            if (this.swiperInstance) {
              this.swiperInstance.update();
            }
          }, 100);
        }
      }
    };

    // 3. Initialize Autoplay if a speed > 0 is provided
    if (this.autoScrollSpeed > 0) {
      swiperOptions.autoplay = {
        delay: this.autoScrollSpeed,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      };
    }

    const swiper = new Swiper(this, swiperOptions);

    this.swiperInstance = swiper;
  }

  destroySwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  onBlockSelect(event) {
    if (!this.swiperInstance) return;

    const slide = event.target;
    const slides = Array.from(this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)'));
    const index = slides.indexOf(slide);

    if (index === -1) return;

    if (this.isLooping) {
      this.swiperInstance.slideToLoop(index);
    } else {
      this.swiperInstance.slideTo(index);
    }
  }
}

// Register Custom Element
if (!customElements.get('featured-slider-component')) {
  customElements.define('featured-slider-component', FeaturedSliderComponent);
}